import type {
    TResponseGetExtraction,
    TResponseGetExtractionAccountMapping,
} from "@/apis/odin/apiODIN";

import type { TDtoTableExtraction } from "./TDtoTableExtraction";
import {
    editedFieldKey,
    formatOperatingStatementLabel,
    generateFieldId,
    originalFieldKey,
} from "./formatUtils";

const isMappedRowWithAccountMappingTypeGuard = (
    mappedRow: TResponseGetExtraction["mappedRows"][number],
): mappedRow is TResponseGetExtraction["mappedRows"][number] & {
    mapping: TResponseGetExtractionAccountMapping & { mappingType: "mapped" };
} => mappedRow.mapping.mappingType === "mapped";

const isMappedRowWittOmitMappingTypeGuard = (
    mappedRow: TResponseGetExtraction["mappedRows"][number],
): mappedRow is TResponseGetExtraction["mappedRows"][number] & {
    mapping: TResponseGetExtractionAccountMapping & { mappingType: "omit" };
} => mappedRow.mapping.mappingType === "omit";

const isMappingInferredDifferentFromActual = (
    mappedRow: TResponseGetExtraction["mappedRows"][number] & {
        mapping: TResponseGetExtractionAccountMapping & {
            mappingType: "mapped";
        };
    },
): boolean => mappedRow.isMappingAltered;

export const convertAPIResponseToDTOTableExtraction = (
    apiResponse: TResponseGetExtraction,
): TDtoTableExtraction => {
    const rowsPermanent = apiResponse.mappedRows.map(
        ({
            rowId,
            mapping,
            name,
            inferenceMapping,
            labelsetMapping,
            sectionType,
            originalRowType,
            flipRowSign,
        }) => {
            const originalMapping =
                mapping.mappingSource === "inference"
                    ? inferenceMapping.account ?? null
                    : labelsetMapping?.account ?? null;
            const originalSectionType: "income" | "expense" | "non-operating" =
                sectionType === "income"
                    ? "income"
                    : sectionType === "expenses"
                      ? "expense"
                      : "non-operating";
            const mappingType =
                mapping.mappingSource === "inference"
                    ? inferenceMapping.mappingType
                    : labelsetMapping?.mappingType ?? mapping.mappingType;

            return {
                rowId,
                originalAccountName: name,
                account: originalMapping,
                rowType: "account" as const,
                mappingSource: mapping.mappingSource,
                mappingType: mappingType,
                currentSectionType: originalSectionType,
                originalSectionType,
                originalRowType: originalRowType,
                flipRowSign: flipRowSign ?? null,
            };
        },
    );
    const rowsWithMappingsAltered = apiResponse.mappedRows
        .filter(isMappedRowWithAccountMappingTypeGuard)
        .filter(isMappingInferredDifferentFromActual)
        .map(mappedRow => {
            const mappingAccountAlteredByUser = mappedRow.mapping
                .account as string;
            return {
                rowId: mappedRow.rowId,
                type: mappedRow.mapping.mappingType,
                account: mappingAccountAlteredByUser,
                reverted: false,
            };
        });
    const rowsOmitted = apiResponse.mappedRows
        .filter(isMappedRowWittOmitMappingTypeGuard)
        .map(mappedRow => ({
            rowId: mappedRow.rowId,
        }));

    const shouldIncludeTotalColumn =
        apiResponse.totalColumn !== null &&
        apiResponse.totalColumn !== undefined &&
        apiResponse.totalColumnCells !== null &&
        apiResponse.totalColumnCells !== undefined;

    const hasActualMonthOrYear =
        apiResponse.monthColumns.some(
            col => col.columnType === "actual_month",
        ) ||
        apiResponse.yearColumns.some(col => col.columnType === "actual_year");
    const hasBudgetMonthOrYear =
        apiResponse.monthColumns.some(
            col => col.columnType === "budget_month",
        ) ||
        apiResponse.yearColumns.some(col => col.columnType === "budget_year");
    const isAtB = hasActualMonthOrYear && hasBudgetMonthOrYear;

    const operatingStatementsYearColumns = apiResponse.yearColumns.map(
        yearColumn => {
            const typeLabel =
                yearColumn.columnType === "actual_year" ? " Actual" : " Budget";
            return {
                label: isAtB
                    ? `${yearColumn.period[0].year}${typeLabel}`
                    : yearColumn.label,
                columnId: yearColumn.columnId,
                year: yearColumn.period[0].year,
                columnType: yearColumn.columnType,
                isEnabled: yearColumn.isEnabled !== false,
            };
        },
    );
    const operatingStatementsMonths = apiResponse.monthColumns.map(
        monthColumn => {
            const dateLabel = formatOperatingStatementLabel(
                monthColumn.period[0],
            );
            const typeLabel =
                monthColumn.columnType === "budget_month"
                    ? " Budget"
                    : " Actual";
            return {
                label: isAtB ? dateLabel + typeLabel : dateLabel,
                columnId: monthColumn.columnId,
                ...monthColumn.period[0],
                columnType: monthColumn.columnType,
                isEnabled: monthColumn.isEnabled !== false,
            };
        },
    );

    const operatingStatementsYearToDate = apiResponse.yearToDateColumns.map(
        yearToDateColumn => ({
            label: yearToDateColumn.label,
            columnId: yearToDateColumn.columnId,
            ...yearToDateColumn.period,
            columnType: yearToDateColumn.columnType,
            isEnabled: yearToDateColumn.isEnabled !== false,
        }),
    );

    const operatingStatementsCustomPeriod = apiResponse.customPeriodColumns.map(
        customPeriodColumn => ({
            label: customPeriodColumn.label,
            columnId: customPeriodColumn.columnId,
            ...customPeriodColumn.period,
            columnType: customPeriodColumn.columnType,
            isEnabled: customPeriodColumn.isEnabled !== false,
        }),
    );

    const unknownColumns = apiResponse.unknownColumns?.map(unknownColumn => ({
        label: unknownColumn.label,
        columnId: unknownColumn.columnId,
        columnType: unknownColumn.columnType,
        display: false,
        isEnabled: unknownColumn.isEnabled !== false,
    }));

    const allOperatingStatementsColumns = [
        ...operatingStatementsYearColumns,
        ...operatingStatementsMonths,
        ...operatingStatementsYearToDate,
        ...operatingStatementsCustomPeriod,
        ...(unknownColumns ?? []),
    ]
        .toSorted((a, b) => a.columnId - b.columnId)
        .map((item, index) => ({ ...item, columnIndex: index }));

    const allOperatingStatementsColumnsWithTotal = [
        ...allOperatingStatementsColumns,
        ...(shouldIncludeTotalColumn
            ? [
                  {
                      label: apiResponse.totalColumn?.label ?? "Total",
                      columnIndex: undefined,
                      month: undefined,
                      columnType: "total" as const,
                      columnId: apiResponse.totalColumn?.columnId,
                      isEnabled: apiResponse.totalColumn?.isEnabled !== false,
                  },
              ]
            : []),
    ];

    const columnDefsOperatingStatements = allOperatingStatementsColumnsWithTotal
        .filter(item => item.columnType !== "unknown")
        .map(item => {
            const { label, columnType, columnIndex, columnId } = item;
            const isYearColumn =
                columnType === "actual_year" || columnType === "budget_year";
            let headerName = label;
            if (isYearColumn && item.year !== undefined) {
                const typeLabel =
                    columnType === "budget_year" ? " Budget" : " Actual";
                headerName = item.year.toString() + typeLabel;
            }
            return {
                headerName,
                field: generateFieldId(columnType, columnIndex),
                columnId,
                isDisabled: "isEnabled" in item ? !item.isEnabled : false,
            };
        });

    // Grid holds values as hundredths (the decimal point is stripped), so parse
    // response strings the same way for both current and original values.
    const toHundredths = (value: string | null | undefined): number | null => {
        if (value === null || value === undefined) return null;
        if (isNaN(Number(value)) || !isFinite(Number(value))) return null;
        return parseInt(value.replace(/[.,\s]/g, ""), 10);
    };

    const editedCellOriginalsByKey: Record<string, string | null> =
        Object.fromEntries(
            (apiResponse.editedCells ?? []).map(
                ({ rowId, columnId, originalAmount }) => [
                    `${rowId}:${columnId}`,
                    originalAmount ?? null,
                ],
            ),
        );
    const hasEditedCell = (key: string) =>
        Object.prototype.hasOwnProperty.call(editedCellOriginalsByKey, key);

    const reduceOperatingStatementsMonthsAndUnknownColumnsWithTotalColumnToFieldValues =
        (rowId: number) =>
            allOperatingStatementsColumnsWithTotal.reduce(
                (acc, item) => {
                    const { columnIndex, columnType, columnId } = item;
                    const isTotalColumn = columnType === "total";

                    const currentColumnCells = isTotalColumn
                        ? apiResponse.totalColumnCells?.[0] // @HACK there is only one total column and one object in `total_column_cells`
                        : apiResponse.cells[columnIndex];

                    const fieldId = generateFieldId(columnType, columnIndex);

                    const editedKey = `${rowId}:${columnId}`;
                    if (columnId !== undefined && hasEditedCell(editedKey)) {
                        acc[editedFieldKey(fieldId)] = true;
                        acc[originalFieldKey(fieldId)] = toHundredths(
                            editedCellOriginalsByKey[editedKey],
                        );
                    }

                    const fieldValue = currentColumnCells?.[rowId] ?? null;
                    const fieldValueAsInteger = toHundredths(fieldValue);
                    acc[fieldId] =
                        fieldValueAsInteger !== null
                            ? fieldValueAsInteger
                            : fieldValue;
                    return acc;
                },
                {} as Record<string, string | number | boolean | null>,
            );

    const apiDocumentTargetRow = apiResponse.documentTargetRow;
    const documentTargetRow =
        apiDocumentTargetRow === null
            ? null
            : {
                  rowId: apiDocumentTargetRow.rowId,
                  totalName: `Original Document - ${apiDocumentTargetRow.name}`,
                  ...allOperatingStatementsColumnsWithTotal.reduce(
                      (acc, item) => {
                          const { columnIndex, columnType, columnId } = item;
                          const fieldId = generateFieldId(
                              columnType,
                              columnIndex,
                          );
                          const fieldValue =
                              columnId === undefined
                                  ? null
                                  : apiDocumentTargetRow.cells[
                                        String(columnId)
                                    ];
                          const fieldValueAsInteger = toHundredths(fieldValue);
                          acc[fieldId] =
                              fieldValueAsInteger !== null
                                  ? fieldValueAsInteger
                                  : fieldValue ?? null;
                          return acc;
                      },
                      {} as Record<string, string | number | null>,
                  ),
              };

    const rowsPermanentWithOperatingStatements: TDtoTableExtraction["rows"] =
        rowsPermanent.map(row => ({
            ...row,
            ...reduceOperatingStatementsMonthsAndUnknownColumnsWithTotalColumnToFieldValues(
                row.rowId,
            ),
        }));
    const totalRows = apiResponse.totalRows.map(totalRow => ({
        rowId: totalRow.rowId,
        totalName: totalRow.name,
        originalRowType: totalRow.originalRowType,
        rowType: "total" as const,
        flipRowSign: totalRow.flipRowSign ?? null,
        isDocumentTarget: totalRow.isDocumentTarget === true,
        ...reduceOperatingStatementsMonthsAndUnknownColumnsWithTotalColumnToFieldValues(
            totalRow.rowId,
        ),
    }));
    const categoryRows = apiResponse.categoryRows.map(categoryRow => ({
        rowId: categoryRow.rowId,
        categoryName: categoryRow.name,
        originalRowType: categoryRow.originalRowType,
        rowType: "category" as const,
    }));
    const inferenceMappings = apiResponse.mappedRows.reduce(
        (acc, row) => {
            acc[row.rowId.toString()] = {
                mappingType: row.inferenceMapping.mappingType,
                mappingSource: "inference",
                account: row.inferenceMapping.account ?? null,
            };
            return acc;
        },
        {} as TDtoTableExtraction["inferenceMappings"],
    );
    return {
        extractionId: apiResponse.extractionId,
        rows: rowsPermanentWithOperatingStatements,
        columnDefsOperatingStatements,
        totalRows,
        categoryRows,
        documentTargetRow,
        rowsWithMappingsAltered: rowsWithMappingsAltered,
        rowsOmitted: rowsOmitted,
        inferenceMappings: inferenceMappings,
    };
};
